import { sanitizeHtml } from "utils/sanitizeHtml";
import { useData } from "data/data-provider";
import { globalJson } from "global/global_json";
import { heroDescriptionStyle, heroScrimFor } from "utils/heroScrim";
import { FC } from "react";

export interface SectionHeroProps {
  className?: string;
}

const SectionHero: FC<SectionHeroProps> = ({ className = "" }) => {
  const { banner, companyDetails } = useData();

  // The banner's floating badge — the pill over the hero image, edited in the
  // FMS as "Floating badge" on the Banner section and stored as `banner_tagline`
  // on CompanyWebsiteDetail (1NOW-1419). There is exactly ONE pill here: the
  // FMS field drives this element rather than adding a second badge.
  // Empty/unset hides it entirely: the backend field is documented as "empty
  // hides the pill", matching the about-us floating badge. No company-name
  // fallback — a blank value means the tenant wants no pill.
  const rawTagline: string = companyDetails?.banner_tagline?.trim() || "";

  // TEMP stopgap — remove once the FMS banner-tagline editor is live in prod.
  // A few tenants carry a design-variant-seeded tagline they want hidden but
  // can't yet blank from FMS; force-hide the pill for them here. Delete this
  // list when the editor ships and the affected tenants have cleared their
  // tagline in FMS.
  const HIDE_BANNER_TAGLINE_COMPANY_IDS = [2060, 2097];
  const tagline: string = HIDE_BANNER_TAGLINE_COMPANY_IDS.includes(
    Number(globalJson?.company_id)
  )
    ? ""
    : rawTagline;

  // Banner text alignment is chosen in the FMS (Section.alignment).
  //   "left"   → text + eyebrow on the left  (legacy default)
  //   "center" → both centered
  //   "right"  → both right-aligned
  // Default to "left" so tenants who haven't set an alignment keep the
  // original layout exactly.
  const alignment: "left" | "center" | "right" =
    banner?.alignment === "center" || banner?.alignment === "right"
      ? banner.alignment
      : "left";

  // Tailwind class fragments per alignment. Kept as flat string maps (not
  // template strings) so Tailwind's JIT can statically detect every class
  // and include it in the bundle — `text-${alignment}` would silently miss.
  const textBlockAlignment: Record<typeof alignment, string> = {
    left: "text-center sm:text-left mr-auto",
    center: "text-center mx-auto",
    right: "text-center sm:text-right ml-auto",
  };
  const eyebrowJustify: Record<typeof alignment, string> = {
    left: "justify-center sm:justify-start",
    center: "justify-center",
    right: "justify-center sm:justify-end",
  };

  // 1NOW-11113: the heading colour is tenant-picked and sits on the photo, so a soft dark patch
  // behind the text keeps any colour readable without darkening the rest of the picture. A
  // missing flag means ON; the FMS switch turns it off. The patch leans toward the text's side
  // (`--scrim-x`), and below `sm` the text is centred whatever the alignment, so it centres too.
  const scrimOn = companyDetails?.banner_scrim_enabled !== false;
  const scrimAnchor: Record<typeof alignment, string> = {
    left: "[--scrim-x:50%] sm:[--scrim-x:0%]",
    center: "[--scrim-x:50%]",
    right: "[--scrim-x:50%] sm:[--scrim-x:100%]",
  };
  // A dark heading gets a light patch and shadow, a light one a dark patch (the ticket's
  // reopen: #0C1E01 on a dark photo stayed unreadable behind a black patch).
  const { scrim: scrimStyle, shadow: shadowStyle } = heroScrimFor(companyDetails?.banner_heading_color);
  const headingShadow = scrimOn ? shadowStyle : undefined;
  // The description sits on the same patch, so it follows the same rule (a pale description on
  // the light patch measured about 2.2:1).
  const descriptionStyle = scrimOn
    ? heroDescriptionStyle(companyDetails?.banner_heading_color, companyDetails?.banner_description_color)
    : undefined;

  return (
    // `w-full` so this fills its flex parent in PageHome.tsx
    // (`<div class="flex-1 flex items-center ...">`). Without it the outer
    // div shrinks to content width and the inner `container mx-auto` has
    // nothing to center within, so the banner content left-aligns to the
    // viewport while the Header (a plain block element) stays centered —
    // making the logo look pushed right relative to the banner heading.
    <div
      className={`nc-SectionHero relative w-full ${className}`}
      data-nc-id="SectionHero"
    >
      {/* Plain `container` — relies on Tailwind's container config for the
          max-width + horizontal padding. Earlier this had an extra
          `px-4 sm:px-8 lg:px-12` on top of `container`, which pushed the
          banner content 32-48px further inset than the logo (which only
          carries 16px from its own container). Removing those overrides so
          the banner heading / eyebrow pill / description left-align with
          the header logo and every section below the fold. */}
      <div className="container pt-10 pb-24 sm:pt-14 lg:pt-20 lg:pb-36 relative">
        {/* Eyebrow tagline (was a hardcoded "Home > For Rent" breadcrumb —
            replaced with a configurable pill driven by FMS data). Justified
            per banner.alignment so the pill follows the heading. */}
        {tagline && (
          <div className={`flex ${eyebrowJustify[alignment]}`}>
            {/* The tagline is drawn in the description colour, so it follows the description's
                contrast rule and the heading's glow (1NOW-11113, 8 Oct reopen: cyan at 1.1:1). */}
            <span className="db-hero__eyebrow" style={scrimOn ? { ...descriptionStyle, ...headingShadow } : undefined}>
              {tagline}
            </span>
          </div>
        )}

        {/* `relative isolate` keeps the scrim behind the text but above the photo. */}
        <div
          className={`relative isolate max-w-xl lg:max-w-[34rem] ${textBlockAlignment[alignment]}`}
        >
          {scrimOn && (
            <div
              aria-hidden
              className={`pointer-events-none absolute -z-10 -inset-x-4 sm:-inset-x-12 -inset-y-8 [--scrim-fx:0rem] sm:[--scrim-fx:3rem] [--scrim-fy:2rem] ${scrimAnchor[alignment]}`}
              style={scrimStyle}
            />
          )}
          {/* Clamp a long banner title to 4 lines so it truncates within the
              fixed-height hero instead of overflowing and bleeding into the
              Vehicles section below (1NOW-1264). `[&_p]:inline` flattens the
              rich-text <p> wrapper so the line-clamp counts text lines, not a
              single block. */}
          <h1
            className="text-[32px] sm:text-5xl lg:text-[52px] font-bold text-banner-heading-color leading-[1.08] tracking-[-0.02em] line-clamp-4 [&_p]:m-0 [&_p]:inline"
            style={headingShadow}
          >
            <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(banner?.heading) }} />
          </h1>

          {/* Clamp a long banner description to 3 lines so it truncates with
              an ellipsis (…) inside the fixed-height hero instead of spilling
              over the image / scroll cue. The clamp lives on this wrapper (not
              the gradient span) so the element is a real `-webkit-box` with a
              visible text color — the auto-generated ellipsis inherits that
              color and actually shows. `[&_p]:inline` flattens the rich-text
              <p> wrapper so line-clamp counts text lines. */}
          <div
            className="mt-4 sm:mt-5 text-banner-description-color line-clamp-3 [&_p]:m-0 [&_p]:inline"
            style={descriptionStyle}
          >
            <span
              className="text-base sm:text-lg !text-banner-description-color font-normal leading-relaxed"
            >
              <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(banner?.description) }} />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionHero;
